import {
  Children,
  DetailedHTMLProps,
  FunctionComponent,
  HTMLAttributes,
  isValidElement,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Components } from "react-markdown";

import { classNames } from "primereact/utils";

import { faArrowsRotate, faCopy, faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ChatExchangeModel, ChatSourceModel, GroundingServiceResponseError } from "../../../../../@api";

import { HtmlMarkdown } from "../../../../components/Shared/Components";
import { TooltipWrapper } from "../../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { translationKeys } from "../../../../models/app-translation-keys";
import { copyTextToClipboard } from "../../../../models/functions/copy-text-to-clipboard.function";
import { useChatService } from "../../../../services/ServiceHooks";
import { useStore } from "../../../../stores/StoreContext";
import { CodeBlock } from "./CodeBlock/CodeBlock";
import { ExchangeAiAvatar } from "./ExchangeAiAvatar/ExchangeAiAvatar";
import { ExchangeUserMetaDataInformation } from "./ExchangeUserMetaDataInformation/ExchangeUserMetaDataInformation";

import "./Exchange.scss";

import { useIsChatDisabled } from "../../Hooks/useIsChatDisabled";

interface IExchangeProps {
  exchangeId?: string;
  content: string;
  isAi: boolean;
  exchange?: ChatExchangeModel;
  sources?: ChatSourceModel[] | null;
  error?: string | null;
  groundingServiceError?: GroundingServiceResponseError | null;
}

interface IProcessedChatSourceModel extends ChatSourceModel {
  sourceInAnswerReference: number;
}

export const Exchange: FunctionComponent<IExchangeProps> = (props: IExchangeProps) => {
  const { t } = useTranslation();
  const { userStore, toastStore, threadStore } = useStore();
  const chatbotService = useChatService();

  const isChatBotDisabled = useIsChatDisabled();

  const [showUserExchangeMetaData, setShowUserExchangeMetaData] = useState<boolean>(false);

  const [filteredSources, setFilteredSources] = useState<IProcessedChatSourceModel[]>([]);
  const [cleanedContentWithSourceBadges, setCleanedContentWithSourceBadges] = useState<string>(props.content);

  useEffect(() => {
    if (!props.sources?.length) {
      setFilteredSources([]);
      setCleanedContentWithSourceBadges(props.content);
      return;
    }

    const filteredAndProcessedSources = getFilteredAndProcessedSources();
    setFilteredSources(filteredAndProcessedSources);

    const contentsWithSourcesSplit = getPolishedAiAnswerWithSources(props.content, filteredAndProcessedSources);
    setCleanedContentWithSourceBadges(contentsWithSourcesSplit);
  }, [props.sources]);

  const getFilteredAndProcessedSources = (): IProcessedChatSourceModel[] => {
    const sources = props.sources?.filter(source => !!source.contentLocation?.length || !!source.title?.length) ?? [];
    return sources.map(
      (source, index: number) =>
        ({
          ...source,
          sourceInAnswerReference: index + 1,
        }) as IProcessedChatSourceModel,
    );
  };

  const getPolishedAiAnswerWithSources = (
    content: string,
    sources: IProcessedChatSourceModel[],
    textual: boolean = false,
  ): string => {
    if (!sources.length) {
      return content;
    }

    let result = "";
    const numberReferenceRegex = /(\[(§\d+§)+\])|(\[(#\d+#)+\])/;

    const splitRegex = new RegExp(numberReferenceRegex, "g");

    let match: RegExpExecArray | null;
    let contentPositionIndex = 0;
    while ((match = splitRegex.exec(content)) !== null) {
      const content = match.input.substring(contentPositionIndex, match.index).trim();
      const fullSourcesOfContentPart: string = match[0];

      const matchedSources: string[] = fullSourcesOfContentPart
        .substring(1, fullSourcesOfContentPart.length - 1)
        .split(",");

      contentPositionIndex = match.index + fullSourcesOfContentPart.length;

      const assignedSources: IProcessedChatSourceModel[] = sources.filter(source =>
        matchedSources.some(matchedSource => source.referenceInAnswer.includes(matchedSource)),
      );

      result +=
        content + (textual ? mappedInlineLinkSources(assignedSources) : mappedInlineBadgeSources(assignedSources));
    }

    if (result.length === 0) {
      return content;
    }

    return result;
  };

  const mappedInlineBadgeSources = (assignedSources: IProcessedChatSourceModel[]): ReactNode =>
    assignedSources.map(source => `<sup data-id=${sourceId(source)}>${source.sourceInAnswerReference}</sup>`).join("");

  const mappedInlineLinkSources = (assignedSources: IProcessedChatSourceModel[]): ReactNode =>
    "(" +
    assignedSources
      .map(source => {
        if (source.contentLocation?.length) {
          return `[${source.title}](${encodeURIComponent(source.contentLocation)})`;
        }

        return `${source.title}`;
      })
      .join(", ") +
    ")";

  const sourceId = (source: IProcessedChatSourceModel, prefix?: string): string =>
    `${prefix ? `${prefix}` : "source"}-${props.exchangeId}-${source.sourceInAnswerReference}`;

  const hyperlinkSource = (source: IProcessedChatSourceModel): ReactNode => (
    <a href={source.contentLocation!} target="_blank" className="hyperlink source-hyperlink" title={source.title}>
      {source.title}
    </a>
  );

  const titleSource = (source: IProcessedChatSourceModel): ReactNode => (
    <span className="source-title source-text">{source.title}</span>
  );

  const mappedSources = (sources: IProcessedChatSourceModel[]): ReactNode[] =>
    sources.map(source => (
      <div key={sourceId(source)} id={sourceId(source)} className="source-badge">
        <span className="source-marker">{source.sourceInAnswerReference}</span>

        {source.contentLocation?.length ? hyperlinkSource(source) : titleSource(source)}
      </div>
    ));

  const handleScrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const sup = (props: DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>) => {
    const sourceId = (props as any)["data-id"];
    const inlineSourceId = "inline-" + sourceId;
    return (
      <span
        className="inline-source-marker"
        id={inlineSourceId}
        onClick={() => handleScrollTo(sourceId)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === "Enter" && handleScrollTo(sourceId)}
      >
        <TooltipWrapper
          targetRef={`#${inlineSourceId}`}
          position="top"
          content={t(translationKeys.feature.chatbot.tooltips.goToSource)}
          showDelay={500}
        />

        {props.children}
      </span>
    );
  };

  const copyAnswerToClipboard = (event: KeyboardEvent<HTMLDivElement> | MouseEvent<HTMLDivElement>) => {
    // Get textual content with sources in textual format for clipboard
    const answerForClipboard = getPolishedAiAnswerWithSources(props.content, filteredSources, true);
    copyTextToClipboard(event, answerForClipboard, t(translationKeys.feature.chatbot.toast.chatbotAnswer), toastStore);
  };

  const retryChatRequest = async () => {
    if (props.exchange?.prompt) {
      const requestThread = threadStore.activeThread;

      await chatbotService.sendChatMessage(requestThread, props.exchange?.prompt);
    }
  };

  const generalErrorOutputMemo = useMemo(
    () => <div className="chat-ai-general-error">{props.error}</div>,
    [props.error],
  );

  // PLANNED #58413 handle the new case 'Aggregated' that was created in #58315
  const groundingErrorOutput = useCallback(() => {
    switch (props.groundingServiceError) {
      case GroundingServiceResponseError.Unknown:
        return translationKeys.feature.chatbot.responseErrors.errorUnknown;

      case GroundingServiceResponseError.MissingGroundingData:
        return translationKeys.feature.chatbot.responseErrors.errorMissingGroundingData;

      case GroundingServiceResponseError.TimeOut:
        return translationKeys.feature.chatbot.responseErrors.errorTimeOut;

      default:
        return "";
    }
  }, [props.groundingServiceError, userStore.currentLanguage]);

  const chatAiReponseControlsMemo = useMemo(
    () => (
      <div className="chat-ai-response-controls">
        <div
          id={`chat-ai-copy-${props.exchangeId}`}
          className="chat-ai-copy-icon"
          onClick={copyAnswerToClipboard}
          tabIndex={0}
          role="button"
          onKeyDown={e => e.key === "Enter" && copyAnswerToClipboard(e)}
        >
          <TooltipWrapper
            targetRef={`#chat-ai-warning-${props.exchangeId}`}
            position="right"
            content={t(groundingErrorOutput())}
          />
          <FontAwesomeIcon icon={faCopy} />
        </div>

        <div
          id={`chat-ai-retry-${props.exchangeId}`}
          className={classNames("chat-ai-retry-icon", {
            disabled: isChatBotDisabled,
          })}
          onClick={() => !isChatBotDisabled && retryChatRequest()}
          tabIndex={0}
          role="button"
          onKeyDown={e => e.key === "Enter" && !isChatBotDisabled && retryChatRequest()}
        >
          <TooltipWrapper
            targetRef={`#chat-ai-copy-${props.exchangeId}`}
            position="bottom"
            content={t(translationKeys.feature.chatbot.actions.copyAnswerToClipboard)}
          />
          <FontAwesomeIcon icon={faArrowsRotate} />
        </div>

        <div id={`chat-ai-warning-${props.exchangeId}`} className="chat-ai-warning-icon">
          <TooltipWrapper
            targetRef={`#chat-ai-retry-${props.exchangeId}`}
            position="bottom"
            content={t(translationKeys.feature.chatbot.actions.retryQuestion)}
          />
          {props.groundingServiceError && <FontAwesomeIcon icon={faTriangleExclamation} />}
        </div>
      </div>
    ),
    [props.error, props.groundingServiceError, userStore.currentLanguage, isChatBotDisabled, filteredSources?.length],
  );

  const pre = ({ children }: DetailedHTMLProps<HTMLAttributes<HTMLPreElement>, HTMLPreElement>) => {
    const codeEl = Children.toArray(children).find(
      (child): child is ReactElement<{ className?: string; children?: ReactNode }> =>
        isValidElement(child) && child.type === "code",
    );

    if (codeEl) {
      const language = codeEl.props.className?.replace("language-", "") || undefined;
      const code = String(codeEl.props.children ?? "").replace(/\n$/, "");
      return <CodeBlock language={language} code={code} />;
    }

    return <pre>{children}</pre>;
  };

  const aiMessageOutputMemo = useMemo(
    () => (
      <div className="ai-message-output">
        <HtmlMarkdown components={{ sup, pre } as Components}>{cleanedContentWithSourceBadges}</HtmlMarkdown>
      </div>
    ),
    [cleanedContentWithSourceBadges],
  );

  const userMessageMemo = useMemo(
    () => (
      <div className="exchange-output-message-user">
        <span>{cleanedContentWithSourceBadges}</span>

        {showUserExchangeMetaData && props.exchange && <ExchangeUserMetaDataInformation exchange={props.exchange} />}
      </div>
    ),
    [props.exchange, showUserExchangeMetaData],
  );

  const aiResponseMessageMemo = useMemo(
    () => (
      <div className="exchange-output-message-ai">
        <ExchangeAiAvatar />

        {!!cleanedContentWithSourceBadges?.length && aiMessageOutputMemo}

        {props.error && generalErrorOutputMemo}

        {!!filteredSources.length && <div className="exchange-output-sources">{mappedSources(filteredSources)}</div>}

        {chatAiReponseControlsMemo}
      </div>
    ),
    [
      props.content,
      props.error,
      props.groundingServiceError,
      userStore.currentLanguage,
      filteredSources?.length,
      isChatBotDisabled,
    ],
  );

  return (
    <div
      className="exchange-row"
      style={{ alignItems: props.isAi ? "flex-start" : "flex-end" }}
      onMouseEnter={() => setShowUserExchangeMetaData(true)}
      onMouseLeave={() => setShowUserExchangeMetaData(false)}
      tabIndex={0}
      role="button"
    >
      {props.isAi ? aiResponseMessageMemo : userMessageMemo}
    </div>
  );
};
